import React, { useState, useEffect } from "react";
import {
  Form,
  Input,
  Button,
  Collapse,
  Card,
  Row,
  Col,
  Steps,
  message,
  Select,
  Modal,
  Table,
  Space,
  Popconfirm,
  Tooltip,
  Checkbox,
  Tag,
} from "antd";
import {
  MinusCircleOutlined,
  PlusOutlined,
  DeleteOutlined,
  EyeOutlined,
  SearchOutlined,
  QuestionCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import Layout from "../layout/AppLayout";
import {
  fetchSolvingProcessesMarketplace,
  CreateNewSolvingProcesses,
  GetSoverOnboarding,
  GetSolvingOnboarding,
  DeleteSolvingOnboarding,
} from "../services/accessAPI";
import "./OnboardingSolvingProcesses.css";

const { TextArea } = Input;
const { Option } = Select;
const { Step } = Steps;

const OnboardingSolvingProcesses = () => {
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(0);
  const [data, setData] = useState([]);
  const [dataSolvers, setDataSolvers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [onboardedSolving, setOnboardedSolving] = useState([]);
  const [filteredSolving, setFilteredSolving] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [stepValidation, setStepValidation] = useState({
    0: false, // Basic Info
    1: false, // Systems & Solvers
    2: false, // Endpoints
  });

  // State for unique options
  const [uniqueInterfaceFromOptions, setUniqueInterfaceFromOptions] = useState([]);
  const [uniqueInterfaceToOptions, setUniqueInterfaceToOptions] = useState([]);
  const [uniqueRealWorldProblem, setUniqueRealWorldProblem] = useState([]);

  // State for form selections
  const [searchText, setSearchText] = useState("");
  const [arrayFormatStates, setArrayFormatStates] = useState({});
  const [arrayFormatOutputStates, setArrayFormatOutputStates] = useState({});

  // Step definitions
  const steps = [
    {
      title: 'Basic Info',
      description: 'Enter basic information',
      icon: <CheckCircleOutlined />,
    },
    {
      title: 'Systems & Solvers',
      description: 'Configure systems and solvers',
      icon: <CheckCircleOutlined />,
    },
    {
      title: 'Endpoints',
      description: 'Configure API endpoints',
      icon: <CheckCircleOutlined />,
    },
  ];

  // Load API data
  useEffect(() => {
    const loadApiConfig = async () => {
      try {
        const apiList = await fetchSolvingProcessesMarketplace();
        if (apiList) {
          setData(apiList);
        } else {
          message.warning("No data available.");
        }
      } catch (error) {
        console.error("Error fetching API list:", error);
        message.error("Failed to load marketplace data.");
      } finally {
        setLoading(false);
      }
    };
    loadApiConfig();
  }, []);

  // load solvers
  useEffect(() => {
    const loadApiConfigSolving = async () => {
      try {
        const api = await GetSoverOnboarding();
        if (api) {
          setDataSolvers(api);
        } else {
          message.warning("No data available.");
        }
      } catch (error) {
        console.error("Error fetching solver list:", error);
        message.error("Failed to load solver data.");
      } finally {
        setLoading(false);
      }
    };
    loadApiConfigSolving();
  }, []);

  useEffect(() => {
    const interfaceToValues = [];
    const interfaceFromValues = [];
    const realWorldProblemValues = [];

    data.forEach((item) => {
      // Process realWorldProblems (directly in item)
      if (item.realWorldProblems && Array.isArray(item.realWorldProblems)) {
        item.realWorldProblems.forEach((problem) => {
          if (problem) {
            realWorldProblemValues.push(problem);
          }
        });
      }

      // Process intermodalInterfaces (directly in item)
      if (item.intermodalInterfaces && Array.isArray(item.intermodalInterfaces)) {
        item.intermodalInterfaces.forEach((interfaceObj) => {
          if (interfaceObj.interfaceTo) {
            interfaceToValues.push(interfaceObj.interfaceTo);
          }
          if (interfaceObj.interfaceFrom) {
            interfaceFromValues.push(interfaceObj.interfaceFrom);
          }
        });
      }
    });
    // Normalize and deduplicate all collected values
    setUniqueInterfaceFromOptions(normalizeAndDeduplicate(interfaceFromValues));
    setUniqueInterfaceToOptions(normalizeAndDeduplicate(interfaceToValues));
    setUniqueRealWorldProblem(normalizeAndDeduplicate(realWorldProblemValues));
  }, [data]);

  // Helper function to normalize and deduplicate strings
  const normalizeAndDeduplicate = (values) => {
    if (!Array.isArray(values)) return []; // Handle non-array inputs

    const uniqueValues = new Set();

    values.forEach((value) => {
      if (value === null || value === undefined) return; // Skip null/undefined

      // Handle both strings and numbers (convert numbers to strings)
      const stringValue = typeof value === "number" ? value.toString() : String(value);

      // Normalization steps:
      const normalized = stringValue
        .replace(/[\u200B-\u200D\uFEFF]/g, "") // Remove invisible characters
        .replace(/\s+/g, " ") // Collapse multiple spaces to single space
        .trim()
        .toLowerCase()
        .replace(/^[a-z]/, (firstChar) => firstChar.toUpperCase()); // Capitalize first letter

      if (normalized) {
        uniqueValues.add(normalized);
      }
    });

    return Array.from(uniqueValues).sort((a, b) => a.localeCompare(b));
  };

  // Validation function for each step
  const validateCurrentStep = async (step) => {
    let fieldsToValidate = [];
    
    switch (step) {
      case 0: // Basic Info
        fieldsToValidate = ['name', 'shortDescription', 'description', 'basePath'];
        break;
      case 1: // Systems & Solvers
        try {
          const values = await form.validateFields();
          if (!values.solvers || values.solvers.length < 1) {
            return false;
          }
          return true;
        } catch {
          return false;
        }
      case 2: // Endpoints
        try {
          const values = await form.validateFields();
          if (!values.endpoints || values.endpoints.length < 1) {
            return false;
          }
          // Validate each endpoint has required fields
          for (let endpoint of values.endpoints) {
            if (!endpoint.httpMethod || !endpoint.path || !endpoint.description) {
              return false;
            }
            if (!endpoint.expectedInput || endpoint.expectedInput.length < 1) {
              return false;
            }
            if (!endpoint.expectedOutput || endpoint.expectedOutput.length < 1) {
              return false;
            }
          }
          return true;
        } catch {
          return false;
        }
      default:
        return true;
    }

    try {
      await form.validateFields(fieldsToValidate);
      return true;
    } catch {
      return false;
    }
  };

  // Handle step navigation
  const handleNext = async () => {
    const isValid = await validateCurrentStep(currentStep);
    if (isValid) {
      setStepValidation(prev => ({ ...prev, [currentStep]: true }));
      setCurrentStep(prev => prev + 1);
    } else {
      message.error('Please fill in all required fields before proceeding.');
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    try {
      const allValues = form.getFieldsValue(true);
      console.log("Form values: ", allValues);

      await form.validateFields();

      const transformedValues = {
        ...allValues,
        realWorldProblems: allValues.realWorldProblems?.length > 0 ? allValues.realWorldProblems : [],
        intermodalInterfaces: allValues.intermodalInterfaces?.length > 0 ? allValues.intermodalInterfaces : [],
        legacySystems: allValues.legacySystems?.length > 0 ? allValues.legacySystems : []
      };

      await CreateNewSolvingProcesses(transformedValues);

      form.resetFields();
      setCurrentStep(0);
      setStepValidation({
        0: false,
        1: false,
        2: false,
      });
      message.success("Created successfully");
    } catch (error) {
      message.error(error.message || "Failed to create solving process");
      console.error("API Error:", error);
    }
  };

  const handleDeleteClick = async () => {
    setLoading(true);
    try {
      const response = await GetSolvingOnboarding();
      setTimeout(() => {
        setOnboardedSolving(response);
        setFilteredSolving(response);
        setLoading(false);
        setIsModalVisible(true);
      }, 500);
    } catch (error) {
      setLoading(false);
      message.error("Failed to fetch onboarded solvers");
    }
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    setSearchText("");
    setFilteredSolving([]);
  };

  const handleSearch = (value) => {
    setSearchText(value);
    const filtered = onboardedSolving.filter(
      (solver) =>
        solver.name.toLowerCase().includes(value.toLowerCase()) ||
        solver.description.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredSolving(filtered);
  };

  const handleDeleteSolving = async (solverId) => {
    try {
      await DeleteSolvingOnboarding(solverId);
      const response = await GetSolvingOnboarding();

      // Update the data without closing modal
      setOnboardedSolving(response);
      setFilteredSolving(response);
      message.success(`Solver deleted successfully`);
    } catch (error) {
      message.error("Failed to delete solver");
    }
  };

  // Step content components
  const renderBasicInfo = () => (
    <div style={{ maxWidth: 1000, margin: '0 auto' }}>
      <Row gutter={16}>
        {[
          ["name", "Name", "We usually have this and the base path matching"],
          ["shortDescription", "Short Description"],
          ["addedValue", "Added Value"],
        ].map(([key, label, placeholder]) => (
          <Col span={12} key={key}>
            <Form.Item
              name={key}
              label={label}
              rules={[
                {
                  required: key !== "addedValue",
                  message: `${label} is required`,
                },
              ]}
            >
              <Input placeholder={key === "name" ? placeholder : undefined} />
            </Form.Item>
          </Col>
        ))}
      </Row>

      <Form.Item
        name="description"
        label="Description"
        rules={[{ required: true, message: "Description is required" }]}
      >
        <TextArea rows={4} />
      </Form.Item>

      {/* Base Path Field */}
      <Form.Item name="basePath" label="Base Path" rules={[{ required: true, message: "Base Path is required" }]}>
        <Input placeholder="/EXT_SP_01 (This needs to be according to what the workflow engine is running, also make sure there are no repeats)" />
      </Form.Item>

      {/* Real World Problems */}
      <div style={{ marginBottom: 16 }}>
        <h4>Real World Problems <span style={{ fontSize: 14, color: '#666' }}>(Optional)</span></h4>
      </div>
      <Form.List name="realWorldProblems">
        {(problemFields, { add: addProblem, remove: removeProblem }) => (
          <>
            {problemFields.map(({ key: problemKey, name: problemName }) => (
              <Row gutter={8} key={problemKey}>
                <Col span={20}>
                  <Form.Item name={problemName} rules={[{ required: true, message: "Adding real-world problems is required, or they must be removed." }]}>
                    <Select
                      placeholder="Select or type problem"
                      showSearch
                      allowClear
                      filterOption={(input, option) =>
                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                      }
                    >
                      {uniqueRealWorldProblem.map((problem, idx) => (
                        <Option key={idx} value={problem}>
                          {problem}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={4}>
                  <Button danger icon={<MinusCircleOutlined />} onClick={() => removeProblem(problemName)}>
                    Remove
                  </Button>
                </Col>
              </Row>
            ))}
            <Form.Item>
              <Button type="dashed" onClick={() => addProblem()} icon={<PlusOutlined />} block>
                Add Problem
              </Button>
            </Form.Item>
          </>
        )}
      </Form.List>

      {/* Intermodal Interfaces */}
      <div style={{ marginBottom: 16 }}>
        <h4>Intermodal Interfaces <span style={{ fontSize: 14, color: '#666' }}>(Optional)</span></h4>
      </div>
      <Form.List name="intermodalInterfaces">
        {(interfaceFields, { add: addInterface, remove: removeInterface }) => (
          <>
            {interfaceFields.map(({ key: interfaceKey, name: interfaceName }) => (
              <Row gutter={8} key={interfaceKey}>
                <Col span={10}>
                  <Form.Item name={[interfaceName, "interfaceFrom"]} rules={[{ required: true, message: "Adding interface From is required, or they must be removed." }]}>
                    <Select placeholder="Select Interface From">
                      {uniqueInterfaceFromOptions.map((interfaceFromItem, idx) => (
                        <Option key={idx} value={interfaceFromItem}>
                          {interfaceFromItem}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={10}>
                  <Form.Item name={[interfaceName, "interfaceTo"]} rules={[{ required: true, message: "Adding interface To is required, or they must be removed." }]}>
                    <Select placeholder="Select Interface To">
                      {uniqueInterfaceToOptions.map((interfaceToItem, idx) => (
                        <Option key={idx} value={interfaceToItem}>
                          {interfaceToItem}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={4}>
                  <Button danger icon={<MinusCircleOutlined />} onClick={() => removeInterface(interfaceName)}>
                    Remove
                  </Button>
                </Col>
              </Row>
            ))}
            <Form.Item>
              <Button type="dashed" onClick={() => addInterface()} icon={<PlusOutlined />} block>
                Add Interface
              </Button>
            </Form.Item>
          </>
        )}
      </Form.List>
    </div>
  );

  const renderSystemsSolvers = () => (
    <div style={{ maxWidth: 800, margin: '0 auto' }}>
      <div style={{ marginBottom: 16 }}>
        <h3>Systems & Solvers Configuration <span style={{ color: 'red' }}>*</span></h3>
        <p style={{ color: '#666', marginBottom: 16 }}>
          Select the solvers and configure legacy systems for your solving process.
        </p>
      </div>

      {/* Solvers Field - Multi Select */}
      <Form.Item
        name="solvers"
        label={
          <span>
            <span style={{ color: "red" }}>*</span> Solvers (<span style={{ color: "red" }}>at least one</span>)
          </span>
        }
        rules={[
          {
            validator: async (_, value) => {
              if (!value || value.length < 1) {
                return Promise.reject(new Error("At least one solver is required"));
              }
            },
          },
        ]}
      >
        <Select
          mode="multiple"
          placeholder="Select solvers"
          showSearch
          allowClear
          filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
          maxTagCount="responsive"
        >
          {dataSolvers.map((solver) => (
            <Option key={solver.id} value={solver.name}>
              {solver.name}
            </Option>
          ))}
        </Select>
      </Form.Item>

      {/* Legacy Systems Field */}
      <div style={{ marginBottom: 16 }}>
        <h4>Legacy Systems <span style={{ fontSize: 14, color: '#666' }}>(Optional)</span></h4>
      </div>
      <Form.List name="legacySystems">
        {(legacyFields, { add: addLegacy, remove: removeLegacy }) => (
          <>
            {legacyFields.map(({ key: legacyKey, name: legacyName }) => (
              <Row gutter={8} key={legacyKey}>
                <Col span={20}>
                  <Form.Item name={legacyName} rules={[{ required: true, message: "Adding Legacy Systems is required, or they must be removed." }]}>
                    <Input placeholder="Enter legacy system" />
                  </Form.Item>
                </Col>
                <Col span={4}>
                  <Button danger icon={<MinusCircleOutlined />} onClick={() => removeLegacy(legacyName)}>
                    Remove
                  </Button>
                </Col>
              </Row>
            ))}
            <Form.Item>
              <Button type="dashed" onClick={() => addLegacy()} icon={<PlusOutlined />} block>
                Add Legacy System
              </Button>
            </Form.Item>
          </>
        )}
      </Form.List>
    </div>
  );

  const renderEndpoints = () => (
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ marginBottom: 16 }}>
        <h3>API Endpoints <span style={{ color: 'red' }}>*</span></h3>
        <p style={{ color: '#666', marginBottom: 16 }}>
          Configure the API endpoints for your solving process. At least one endpoint is required.
        </p>
      </div>

      <Form.List
        name="endpoints"
        rules={[
          {
            validator: async (_, value) => {
              if (!value || value.length < 1) {
                return Promise.reject(new Error("At least one endpoint is required"));
              }
            },
          },
        ]}
      >
        {(fields, { add, remove }) => (
          <>
            <Collapse
              accordion
              items={fields.map((field, index) => ({
                key: field.key,
                label: `Endpoint #${index + 1}`,
                extra: (
                  <Button
                    danger
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      remove(field.name);
                    }}
                  >
                    Remove
                  </Button>
                ),
                children: (
                  <>
                    <Row gutter={16}>
                      <Col span={8}>
                        <Form.Item
                          name={[field.name, "httpMethod"]}
                          label="HTTP Method"
                          rules={[{ required: true, message: "HTTP method is required" }]}
                        >
                          <Select placeholder="Select Method">
                            <Option value="GET">GET</Option>
                            <Option value="POST">POST</Option>
                            <Option value="PUT">PUT</Option>
                            <Option value="DELETE">DELETE</Option>
                            <Option value="PATCH">PATCH</Option>
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col span={16}>
                        <Form.Item name={[field.name, "path"]} label="Path" rules={[{ required: true, message: "Path is required" }]}>
                          <Input placeholder="/my-endpoint" />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Form.Item
                      name={[field.name, "description"]}
                      label="Description"
                      rules={[{ required: true, message: "Description is required" }]}
                    >
                      <Input.TextArea rows={2} />
                    </Form.Item>

                    <Card
                      type="inner"
                      title={
                        <div style={{ display: "flex", alignItems: "center", gap: 4, whiteSpace: "nowrap" }}>
                          <span style={{ color: "red" }}>*</span>
                          <span>Expected Input</span>
                          <span>(<span style={{ color: "red" }}>at least one</span>)</span>
                        </div>
                      }
                      style={{ marginBottom: 12, overflow: "auto", height: 'auto' }}
                    >
                      <Form.List
                        name={[field.name, "expectedInput"]}
                        rules={[
                          {
                            validator: async (_, value) => {
                              if (!value || value.length < 1) {
                                return Promise.reject(new Error("At least one input is required"));
                              }
                            },
                          },
                        ]}
                      >
                        {(inputFields, { add: addInput, remove: removeInput }) => (
                          <>
                            {inputFields.map(({ key: inputKey, name: inputName }) => (
                              <Row gutter={16} key={inputKey}>
                                <Col span={4}>
                                  <Form.Item name={[inputName, "name"]} rules={[{ required: true, message: "Name is required"}]}>
                                    <Input placeholder="Name" />
                                  </Form.Item>
                                </Col>
                                <Col span={4}>
                                  <Form.Item name={[inputName, "format"]} rules={[{ required: true, message: "Format is required"}]}>
                                    <Input placeholder="Format" />
                                  </Form.Item>
                                </Col>
                                <Col span={4}>
                                  <Form.Item name={[inputName, "in"]} rules={[{ required: true, message: "In is required" }]}>
                                    <Select placeholder="In">
                                      <Option value="query">query</Option>
                                      <Option value="body">body</Option>
                                      <Option value="form">form-data</Option>
                                      <Option value="header">header</Option>
                                      <Option value="path">path</Option>
                                      <Option value="string">string</Option>
                                      <Option value="int">integer</Option>
                                      <Option value="null">null</Option>
                                    </Select>
                                  </Form.Item>
                                </Col>
                                <Col span={4}>
                                  <Form.Item
                                    rules={[{ required: true, message: "Unit is required" }]}
                                    name={[inputName, "unit"]}
                                    normalize={(value) => value?.trim() || ""}
                                    tooltip={{
                                      title: arrayFormatStates[inputKey] ? (
                                        <div>
                                          <div><strong>Unit Input Format:</strong></div>
                                          <div>• Use square brackets: [item1, item2, item3]</div>
                                          <div>• Separate items with commas</div>
                                          <div>• Examples: [1,2,3] or [kg, lbs, oz]</div>
                                        </div>
                                      ) : (
                                        <div>
                                          <div><strong>Unit Input Format:</strong></div>
                                          <div>• Enter a simple string</div>
                                          <div>• Examples: kg, meters, pieces</div>
                                          <div>• Leave empty for null</div>
                                        </div>
                                      ),
                                      icon: <QuestionCircleOutlined />,
                                    }}
                                  >
                                    <Input
                                      placeholder={
                                        arrayFormatStates[inputKey]
                                          ? "Enter unit in [item1, item2, item3] format"
                                          : "Enter unit as string (e.g., kg, meters) or leave empty for null"
                                      }
                                      allowClear
                                    />
                                  </Form.Item>
                                  <Form.Item>
                                    <Checkbox
                                      checked={arrayFormatStates[inputKey] || false}
                                      onChange={(e) => setArrayFormatStates(prev => ({
                                        ...prev,
                                        [inputKey]: e.target.checked
                                      }))}
                                    >
                                      Enum
                                    </Checkbox>
                                  </Form.Item>
                                </Col>
                                <Col span={4}>
                                  <Form.Item name={[inputName, "label"]} rules={[{ required: true, message: "Label is required" }]}>
                                    <Input placeholder="Label" />
                                  </Form.Item>
                                </Col>
                                <Col span={2}>
                                  <Button
                                    icon={<MinusCircleOutlined />}
                                    danger
                                    onClick={() => removeInput(inputName)}
                                  />
                                </Col>
                              </Row>
                            ))}
                            <Form.Item>
                              <Button type="dashed" onClick={() => addInput()} icon={<PlusOutlined />} block>
                                Add Expected Input
                              </Button>
                            </Form.Item>
                          </>
                        )}
                      </Form.List>
                    </Card>

                    <Card
                      type="inner"
                      title={
                        <div style={{ display: "flex", alignItems: "center", gap: 4, whiteSpace: "nowrap" }}>
                          <span style={{ color: "red" }}>*</span>
                          <span>Expected Output</span>
                          <span>(<span style={{ color: "red" }}>at least one</span>)</span>
                        </div>
                      }
                      style={{ marginBottom: 12, height: 'auto' }}
                    >
                      <Form.List
                        name={[field.name, "expectedOutput"]}
                        rules={[
                          {
                            validator: async (_, value) => {
                              if (!value || value.length < 1) {
                                return Promise.reject(new Error("At least one output is required"));
                              }
                            },
                          },
                        ]}
                      >
                        {(outputFields, { add: addOutput, remove: removeOutput }) => (
                          <>
                            {outputFields.map(({ key: outputKey, name: outputName }) => (
                              <Row gutter={16} key={outputKey}>
                                <Col span={4}>
                                  <Form.Item name={[outputName, "name"]} rules={[{ required: true, message: "Name is required"}]}>
                                    <Input placeholder="Name" />
                                  </Form.Item>
                                </Col>
                                <Col span={4}>
                                  <Form.Item name={[outputName, "format"]} rules={[{ required: true, message: "Format is required" }]}>
                                    <Input placeholder="Format" />
                                  </Form.Item>
                                </Col>
                                <Col span={4}>
                                  <Form.Item name={[outputName, "in"]} rules={[{ required: true, message: "In is required" }]}>
                                    <Select placeholder="In">
                                      <Option value="query">query</Option>
                                      <Option value="body">body</Option>
                                      <Option value="form">form-data</Option>
                                      <Option value="header">header</Option>
                                      <Option value="path">path</Option>
                                      <Option value="string">string</Option>
                                      <Option value="int">integer</Option>
                                      <Option value="null">null</Option>
                                    </Select>
                                  </Form.Item>
                                </Col>
                                <Col span={4}>
                                  <Form.Item
                                    rules={[{ required: true, message: "Unit is required" }]}
                                    name={[outputName, "unit"]}
                                    normalize={(value) => value?.trim() || ""}
                                    tooltip={{
                                      title: arrayFormatOutputStates[outputKey] ? (
                                        <div>
                                          <div><strong>Unit Input Format:</strong></div>
                                          <div>• Use square brackets: [item1, item2, item3]</div>
                                          <div>• Separate items with commas</div>
                                          <div>• Examples: [1,2,3] or [kg, lbs, oz]</div>
                                        </div>
                                      ) : (
                                        <div>
                                          <div><strong>Unit Input Format:</strong></div>
                                          <div>• Enter a simple string</div>
                                          <div>• Examples: kg, meters, pieces</div>
                                          <div>• Leave empty for null</div>
                                        </div>
                                      ),
                                      icon: <QuestionCircleOutlined />,
                                    }}
                                  >
                                    <Input
                                      placeholder={
                                        arrayFormatOutputStates[outputKey] 
                                          ? "Enter unit in [item1, item2, item3] format"
                                          : "Enter unit as string (e.g., kg, meters) or leave empty for null"
                                      }
                                      allowClear
                                    />
                                  </Form.Item>
                                  <Form.Item>
                                    <Checkbox
                                      checked={arrayFormatOutputStates[outputKey] || false}
                                      onChange={(e) => setArrayFormatOutputStates(prev => ({
                                        ...prev,
                                        [outputKey]: e.target.checked
                                      }))}
                                    >
                                      Enum
                                    </Checkbox>
                                  </Form.Item>
                                </Col>
                                <Col span={4}>
                                  <Form.Item name={[outputName, "label"]} rules={[{ required: true, message: "Label is required" }]}>
                                    <Input placeholder="Label" />
                                  </Form.Item>
                                </Col>
                                <Col span={2}>
                                  <Button
                                    icon={<MinusCircleOutlined />}
                                    danger
                                    onClick={() => removeOutput(outputName)}
                                  />
                                </Col>
                              </Row>
                            ))}
                            <Form.Item>
                              <Button type="dashed" onClick={() => addOutput()} icon={<PlusOutlined />} block>
                                Add Expected Output
                              </Button>
                            </Form.Item>
                          </>
                        )}
                      </Form.List>
                    </Card>
                  </>
                ),
              }))}
            />
            <Form.Item style={{ marginTop: 16 }}>
              <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />} block>
                Add Endpoint
              </Button>
            </Form.Item>
          </>
        )}
      </Form.List>
    </div>
  );

  // Get step content based on current step
  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return renderBasicInfo();
      case 1:
        return renderSystemsSolvers();
      case 2:
        return renderEndpoints();
      default:
        return null;
    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (text) => (
        <Space>
          <span style={{ fontWeight: "bold" }}>{text}</span>
        </Space>
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: "50%",
    },
    {
      title: "Action",
      key: "action",
      width: 150,
      render: (_, record) => (
        <Popconfirm
          title="Delete Solving Process"
          description="Are you sure you want to delete this solving process?"
          onConfirm={() => handleDeleteSolving(record.id)}
          okText="Yes"
          cancelText="No"
          okType="danger"
        >
          <Button type="primary" danger icon={<DeleteOutlined />}>
            Delete
          </Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <Layout>
      <Card
        title={
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span>Solving Processes Onboarding - Step by Step</span>
            <Button
              type="primary"
              danger
              icon={<DeleteOutlined />}
              onClick={handleDeleteClick}
              loading={loading}
            >
              Manage Solving Processes
            </Button>
          </div>
        }
        bordered
        style={{
          height: 'auto',
          width: "100%",
          padding: 20,
          borderRadius: 10,
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
          backgroundColor: "#ffffff",
        }}
      >
        <Form
          form={form}
          layout="vertical"
          style={{ width: '100%' }}
        >
          {/* Progress Steps */}
          <div style={{ marginBottom: 32 }}>
            <Steps 
              current={currentStep} 
              size="small"
              items={steps.map((step, index) => ({
                ...step,
                status: stepValidation[index] 
                  ? 'finish' 
                  : index === currentStep 
                    ? 'process' 
                    : 'wait',
                icon: stepValidation[index] 
                  ? <CheckCircleOutlined style={{ color: '#52c41a' }} />
                  : index === currentStep
                    ? <ExclamationCircleOutlined style={{ color: '#1890ff' }} />
                    : step.icon
              }))}
            />
          </div>

          {/* Step Content */}
          <div style={{ 
            minHeight: '400px', 
            padding: '24px 0',
            borderRadius: '8px',
            backgroundColor: '#fafafa',
            marginBottom: '24px'
          }}>
            {getStepContent(currentStep)}
          </div>

          {/* Navigation Buttons */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            borderTop: '1px solid #f0f0f0',
            paddingTop: '16px'
          }}>
            <Button 
              onClick={handlePrevious} 
              disabled={currentStep === 0}
              size="large"
            >
              Previous
            </Button>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ color: '#666', fontSize: '14px' }}>
                Step {currentStep + 1} of {steps.length}
              </span>
            </div>

            {currentStep < steps.length - 1 ? (
              <Button 
                type="primary" 
                onClick={handleNext}
                size="large"
              >
                Next
              </Button>
            ) : (
              <Button 
                type="primary" 
                onClick={handleSubmit}
                size="large"
                style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
              >
                Submit
              </Button>
            )}
          </div>
        </Form>
      </Card>

      {/* Modal for managing solving processes */}
      <Modal
        title={
          <Space>
            <EyeOutlined />
            <span>Onboarded Solving Processes</span>
          </Space>
        }
        open={isModalVisible}
        onCancel={handleModalClose}
        footer={[
          <Button key="close" onClick={handleModalClose}>
            Close
          </Button>,
        ]}
        width={1000}
      >
        <div style={{ marginBottom: "16px" }}>
          <Input
            placeholder="Search by name or description..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => handleSearch(e.target.value)}
            allowClear
            style={{ width: "100%" }}
          />
        </div>

        <Table
          columns={columns}
          dataSource={filteredSolving}
          rowKey="id"
          pagination={{
            pageSize: 8,
            showSizeChanger: false,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} solving processes`,
          }}
          locale={{
            emptyText: searchText
              ? `No solving processes found matching "${searchText}"`
              : "No onboarded solving processes found",
          }}
        />
      </Modal>
    </Layout>
  );
};

export default OnboardingSolvingProcesses;